// owner: social-graph domain agent — zod schemas + inferred types shared by api and web
import { z } from 'zod';
import {
  EpochMsSchema,
  HandleSchema,
  PaginationQuerySchema,
  PersonaSchema,
  UlidSchema,
  UrlSchema,
  VisibilitySchema,
  paginated,
} from './common';
import { VerifiedBadgeSchema } from './verification';

/** Handles that would collide with routes or impersonate the platform. Compared lower-case. */
export const RESERVED_HANDLES: readonly string[] = [
  'admin',
  'administrator',
  'api',
  'app',
  'auth',
  'avatars',
  'help',
  'jobs',
  'flory',
  'login',
  'logout',
  'mcp',
  'me',
  'news',
  'null',
  'official',
  'privacy',
  'root',
  'search',
  'security',
  'settings',
  'signup',
  'staff',
  'support',
  'system',
  'terms',
  'undefined',
  'www',
];

export const ClaimHandleSchema = z.object({
  handle: HandleSchema.refine((h) => !RESERVED_HANDLES.includes(h), 'This handle is reserved'),
});
export type ClaimHandle = z.infer<typeof ClaimHandleSchema>;

/** Derive a valid, non-reserved handle candidate (without uniqueness suffix) from free text. */
export function handleCandidate(source: string, fallback = 'member'): string {
  let base = source
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30)
    .replace(/-+$/g, '');
  if (base.length < 4) base = `${base || fallback}-${'0000'}`.slice(0, 30).replace(/-+$/g, '');
  if (RESERVED_HANDLES.includes(base)) base = `${base}-user`;
  return base;
}

const Short = (max: number) => z.string().trim().max(max);
export const SkillSchema = z.string().trim().min(1).max(50);
export const OpenToSchema = z.enum([
  'work',
  'hiring',
  'mentoring',
  'speaking',
  'freelance',
  'collaboration',
]);
export const MonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Expected YYYY-MM');

export const ExperienceInputSchema = z
  .object({
    companyName: Short(120).min(1),
    title: Short(120).min(1),
    orgId: UlidSchema.nullish(),
    location: Short(120).default(''),
    description: Short(2000).default(''),
    startDate: MonthSchema,
    endDate: MonthSchema.nullish(),
    sortOrder: z.number().int().min(0).max(1000).default(0),
  })
  .refine((v) => !v.endDate || v.endDate >= v.startDate, {
    path: ['endDate'],
    message: 'endDate must not be before startDate',
  });
export const ExperiencePatchSchema = z
  .object({
    companyName: Short(120).min(1),
    title: Short(120).min(1),
    orgId: UlidSchema.nullable(),
    location: Short(120),
    description: Short(2000),
    startDate: MonthSchema,
    endDate: MonthSchema.nullable(),
    sortOrder: z.number().int().min(0).max(1000),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Provide at least one field to change');
export const ExperienceSchema = z.object({
  id: UlidSchema,
  orgId: UlidSchema.nullable(),
  companyName: z.string(),
  title: z.string(),
  location: z.string(),
  description: z.string(),
  startDate: z.string(),
  endDate: z.string().nullable(),
  sortOrder: z.number().int(),
});

export const EducationInputSchema = z
  .object({
    school: Short(160).min(1),
    degree: Short(120).default(''),
    field: Short(120).default(''),
    startDate: MonthSchema.nullish(),
    endDate: MonthSchema.nullish(),
    sortOrder: z.number().int().min(0).max(1000).default(0),
  })
  .refine((v) => !v.startDate || !v.endDate || v.endDate >= v.startDate, {
    path: ['endDate'],
    message: 'endDate must not be before startDate',
  });
export const EducationPatchSchema = z
  .object({
    school: Short(160).min(1),
    degree: Short(120),
    field: Short(120),
    startDate: MonthSchema.nullable(),
    endDate: MonthSchema.nullable(),
    sortOrder: z.number().int().min(0).max(1000),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Provide at least one field to change');
export const EducationSchema = z.object({
  id: UlidSchema,
  school: z.string(),
  degree: z.string(),
  field: z.string(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  sortOrder: z.number().int(),
});

export const ProfilePatchSchema = z
  .object({
    displayName: Short(120).min(1),
    headline: Short(220),
    about: Short(5000),
    location: Short(120),
    skills: z.array(SkillSchema).max(50),
    openTo: z.array(OpenToSchema).max(10),
    personas: z.array(PersonaSchema).max(8),
    website: UrlSchema.max(2048).nullable(),
    visibility: VisibilitySchema,
    seoEnabled: z.boolean(),
    geoEnabled: z.boolean(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Provide at least one field to change');
export type ProfilePatch = z.infer<typeof ProfilePatchSchema>;

const ProfileCore = {
  userId: UlidSchema,
  handle: z.string(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  headline: z.string(),
  about: z.string(),
  location: z.string(),
  skills: z.array(z.string()),
  openTo: z.array(z.string()),
  website: z.string().nullable(),
  seoEnabled: z.boolean(),
  geoEnabled: z.boolean(),
  visibility: VisibilitySchema,
  experiences: z.array(ExperienceSchema),
  educations: z.array(EducationSchema),
  createdAt: EpochMsSchema,
  updatedAt: EpochMsSchema,
};

/** The signed-in owner's own profile (includes personas). */
export const OwnProfileSchema = z.object({ ...ProfileCore, personas: z.array(z.string()) });
export type OwnProfile = z.infer<typeof OwnProfileSchema>;

/** What anyone allowed to see the profile gets. Never contains email. */
export const PublicProfileSchema = z.object({
  ...ProfileCore,
  /** PRIVACY: redacted (domain only, never the full verified email) — see VerifiedBadgeSchema. */
  verifiedBadges: z.array(VerifiedBadgeSchema),
  counts: z.object({ connections: z.number().int(), followers: z.number().int() }),
  viewer: z
    .object({
      isSelf: z.boolean(),
      connection: z.enum(['none', 'pending_in', 'pending_out', 'connected']),
      following: z.boolean(),
      /** A17: 1st (direct connection), 2nd (mutual connection), 3rd+ (further or no path found). */
      degree: z.union([z.literal(1), z.literal(2), z.literal(3)]),
      mutualCount: z.number().int().nonnegative(),
    })
    .nullable(),
});
export type PublicProfile = z.infer<typeof PublicProfileSchema>;

export const ProfileHandleParamsSchema = z.object({ handle: z.string().min(1).max(60) });

export const ProfileSearchQuerySchema = PaginationQuerySchema.extend({
  q: z.string().trim().max(200).optional(),
  location: z.string().trim().max(120).optional(),
  skill: SkillSchema.optional(),
  openTo: OpenToSchema.optional(),
});
export const ProfileSummarySchema = z.object({
  userId: UlidSchema,
  handle: z.string(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  headline: z.string(),
  location: z.string(),
  skills: z.array(z.string()),
  seoEnabled: z.boolean(),
  geoEnabled: z.boolean(),
});
export type ProfileSummary = z.infer<typeof ProfileSummarySchema>;
export const ProfileSearchResponseSchema = paginated(ProfileSummarySchema);

export const IdParamsSchema = z.object({ id: UlidSchema });
export const DeletedSchema = z.object({ deleted: z.literal(true) });

export const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
export const AVATAR_TYPES = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
} as const;
export const AvatarResponseSchema = z.object({
  avatarUrl: z.string(),
  mediaId: UlidSchema,
  contentType: z.string(),
  sizeBytes: z.number().int(),
});
export const AvatarParamsSchema = z.object({ mediaId: UlidSchema });

// ------------------------------------------------------------------ resumes

export const RESUME_MAX_BYTES = 5 * 1024 * 1024;
export const RESUME_TYPES = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
} as const;
/** A resume is private: it is only ever downloaded via the application it was attached to. */
export const ResumeSchema = z.object({
  mediaId: UlidSchema,
  contentType: z.string(),
  sizeBytes: z.number().int(),
  fileName: z.string().nullable(),
  createdAt: EpochMsSchema,
});
export type Resume = z.infer<typeof ResumeSchema>;
export const ResumesSchema = z.object({ items: z.array(ResumeSchema) });
export const ResumeParamsSchema = z.object({ mediaId: UlidSchema });

// ------------------------------------------------------------- endorsements

export const UserIdParamSchema = z.object({ userId: UlidSchema });
export const EndorseBodySchema = z.object({ skill: SkillSchema });
export const EndorseSkillParamsSchema = z.object({ userId: UlidSchema, skill: Short(50).min(1) });
export const EndorseResponseSchema = z.object({
  skill: z.string(),
  count: z.number().int().nonnegative(),
});
export const EndorsementItemSchema = z.object({
  skill: z.string(),
  count: z.number().int().nonnegative(),
  endorsedByMe: z.boolean(),
});
export const EndorsementsSchema = z.object({ items: z.array(EndorsementItemSchema) });

// ---------------------------------------------------------- recommendations

export const RecommendationBodySchema = z.object({ body: z.string().trim().min(1).max(3000) });
export const RecommendationStatusSchema = z.enum(['pending', 'accepted', 'hidden']);
export const RecommendationSchema = z.object({
  id: UlidSchema,
  authorId: UlidSchema,
  subjectId: UlidSchema,
  body: z.string(),
  status: RecommendationStatusSchema,
  createdAt: EpochMsSchema,
  updatedAt: EpochMsSchema,
});
export type Recommendation = z.infer<typeof RecommendationSchema>;
export const RecommendationsSchema = z.object({ items: z.array(RecommendationSchema) });
export const RecommendationIdParamSchema = z.object({ id: UlidSchema });

/** Account deletion requires typing DELETE: irreversible. */
export const DeleteAccountSchema = z.object({ confirm: z.literal('DELETE') });

export const ExportSchema = z.object({
  exportedAt: EpochMsSchema,
  userId: UlidSchema,
  schemaVersion: z.literal(1),
  tables: z.record(z.string(), z.array(z.record(z.string(), z.unknown()))),
});
export type DataExport = z.infer<typeof ExportSchema>;

/**
 * Turns free text into a safe FTS5 MATCH expression: every alphanumeric token becomes a quoted
 * prefix term (AND-ed). Returns null when nothing searchable remains. Never passes operators through.
 */
export function buildFtsQuery(input: string, maxTokens = 8): string | null {
  const tokens = input.match(/[\p{L}\p{N}]+/gu)?.slice(0, maxTokens);
  if (!tokens?.length) return null;
  return tokens.map((t) => `"${t}"*`).join(' ');
}
